// src/components/ApiKeyManagement.tsx
import React, { useState, useEffect, useRef } from 'react';
import { fetchApiKeys, deleteApiKey, createApiKey, ApiKey } from '../../utils/apiKeyService';
import ApiKeyModal from './ApiKeyModal';
import { AddStrongIcon } from 'src/monet/icons';
import { TrashIcon } from '@heroicons/react/outline';
import { Placement } from '@popperjs/core';
import { Popover } from '@headlessui/react';
import { useCurrentSubscription } from 'src/store/subscription';
import OrganizationLink from 'src/common/OrganizationLink';
import classNames from 'classnames';

const ApiKeyManagement: React.FC = () => {
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [newApiKey, setNewApiKey] = useState<ApiKey | null>(null);
  const [showModal, setShowModal] = useState(false);

  const [isLowerPlanGated, setLowerPlanGated] = useState(false);
  const menuPlacementRef = useRef<Placement>();
  const {isPro, isOnTrial, isEnterprise} = useCurrentSubscription(state => state);


  const getApiKeys = async () => {
    const keysResponse = await fetchApiKeys();
    if(keysResponse.api_keys){
      setApiKeys(keysResponse.api_keys);
    }
    else{
      setApiKeys([])
    }
  };

  useEffect(()=>{

    if(isEnterprise || isOnTrial){
      setLowerPlanGated(false);
    }
    else{
      setLowerPlanGated(true);
    }
  },[isOnTrial,isEnterprise])

  useEffect(() => {
    getApiKeys();
  }, []);

  const handleDelete = async (api_key: string) => {
    await deleteApiKey(api_key);
    setApiKeys(apiKeys.filter(key => key.api_key !== api_key));
  };

  const handleCreate = async () => {
    const key = await createApiKey();
    setNewApiKey(key);
    setShowModal(true);
    await getApiKeys();
  };

  const hideSecret = (secret: string) => {
    return `${secret.substring(0, 2)}********${secret.substring(secret.length - 4)}`;
  };

  return (
    <div className="p-2">
        <div className='flex'>
      <p className="text-bedrock-p my-auto">API Key Management</p>
 
   

              <button
          type='button'
          disabled={isLowerPlanGated}
          className={classNames('flex btn-bedrock-secondary-small items-center ml-4',isLowerPlanGated?'cursor-not-allowed':'cursor-pointer')}
          onClick={handleCreate}
          >
          <AddStrongIcon />
          <span className='ml-1'>Create new API key</span>
        </button>

              </div>     

      <ul className="mt-4">
        {
          isLowerPlanGated?
        <div className='flex flex-col space-y-6 max-w-[600px]'>
        <div className='col-span-5 flex flex-col space-y-2'>
          <h2 className='text-bedrock-p-strong'>How does it work?</h2>
            <p className='text-bedrock-p'>You can use API keys to integrate platforms like Clay with Journey<br/><br/>Personalize your outreach campaigns at scale by creating journeys in bulk with custom logo, custom message and name of the prospect which would appear customized to prospects when they view the journey</p>
        </div>
       
          <div className='space-y-6'>
          
          <OrganizationLink to='/billing'>
              <button className='btn-bedrock-primary self-start flex space-x-1'>
                <span>Upgrade to Scale Plan</span>
              </button>
          </OrganizationLink>
          </div>
      </div>
      :
        apiKeys && apiKeys.map((key,index) => (
          <li key={index} className="flex justify-between max-w-sm items-center bg-gray-100 p-2 my-2 rounded">
            <span>{hideSecret(key.api_key)}</span>
           
            <Popover className="relative">
              <Popover.Button className="text-red-500 hover:text-red-700">
                <TrashIcon className='w-5 h-5' />
              </Popover.Button>
              <Popover.Panel className="absolute z-10 bg-white rounded-lg border border-bedrock-gray-medium py-4 child:px-4 w-60">
              { ({close}) => (
              <div className='flex flex-col space-y-md'>
              <div className='text-bedrock-p text-bedrock-black cursor-default'>Are you sure you want to delete this key?</div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => {
                        handleDelete(key.api_key)
                        close()
                      }
                      }
                      className="btn-bedrock-red"
                    >
                      Yes
                    </button>
                    <Popover.Button className="btn-bedrock-secondary">
                      No
                    </Popover.Button>
                  </div>
                </div>
              ) }
              </Popover.Panel>
            </Popover>
       
              
          </li>
        ))}
      </ul>
      {showModal && newApiKey && (
        <ApiKeyModal apiKey={newApiKey} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};

export default ApiKeyManagement;
